import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, catchError, tap } from 'rxjs';
import {
  ApiListResponse,
  ApiResponse,
} from '../../core/models/api-response.models';
import { ErrorHandlingService } from '../../core/services/error-handling.service';
import { environment } from '../../../environments/environment';
import {
  AuditLogDetailDto,
  AuditLogDto,
  AuditLogQuery,
  AuditModules,
  ChildOrganizationDto,
  ChildOrganizationQuery,
  LogAuditRequest,
  UserByOrganizationDto,
  UserByOrganizationQuery,
} from './audit-logs.interface';
import {
  AUDIT_LOG_LIST_ENDPOINT,
  getAuditLogByIdEndpoint,
  AUDIT_LOG_LOG_AUDIT_ENDPOINT,
} from './audit-logs.constants';

@Injectable({ providedIn: 'root' })
export class AuditLogsService {
  private readonly errorHandler = inject(ErrorHandlingService);
  private readonly http = inject(HttpClient);
  private readonly BASE_URL = environment.apiUrl;
  private readonly LIST_ENDPOINT = `${this.BASE_URL}${AUDIT_LOG_LIST_ENDPOINT}`;
  private readonly LOG_AUDIT_ENDPOINT = `${this.BASE_URL}${AUDIT_LOG_LOG_AUDIT_ENDPOINT}`;
  private readonly auditLogsSubject =
    new BehaviorSubject<ApiListResponse<AuditLogDto> | null>(null);
  auditLogs$ = this.auditLogsSubject.asObservable();

  clearAuditLogs(): void {
    this.auditLogsSubject.next(null);
  }

  getAuditLogs(query: AuditLogQuery): Observable<ApiListResponse<AuditLogDto>> {
    return this.fetchAuditLogs(query).pipe(
      tap(response => this.auditLogsSubject.next(response))
    );
  }

  getAuditLogsForExport(
    query: AuditLogQuery
  ): Observable<ApiListResponse<AuditLogDto>> {
    return this.fetchAuditLogs(query);
  }

  private fetchAuditLogs(
    query: AuditLogQuery
  ): Observable<ApiListResponse<AuditLogDto>> {
    return this.http
      .get<ApiListResponse<AuditLogDto>>(this.LIST_ENDPOINT, {
        params: this.buildQueryParams(query),
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getLoginLogs(query: AuditLogQuery): Observable<ApiListResponse<AuditLogDto>> {
    return this.getAuditLogs({
      ...query,
      auditOperationTypeId: undefined,
      excludeLoginLogs: false,
    });
  }

  getActivityLogs(
    query: AuditLogQuery
  ): Observable<ApiListResponse<AuditLogDto>> {
    return this.getAuditLogs({
      ...query,
      excludeLoginLogs: true,
    });
  }

  getAuditLogById(id: number): Observable<ApiResponse<AuditLogDetailDto>> {
    return this.http
      .get<
        ApiResponse<AuditLogDetailDto>
      >(`${this.BASE_URL}${getAuditLogByIdEndpoint(id)}`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  logAudit(payload: LogAuditRequest): Observable<ApiResponse<string> | null> {
    const normalizedPayload: LogAuditRequest = {
      ...payload,
      organizationIdOverride:
        payload.organizationIdOverride === 0
          ? null
          : payload.organizationIdOverride,
    };
    return this.http
      .post<ApiResponse<string> | null>(
        this.LOG_AUDIT_ENDPOINT,
        normalizedPayload
      )
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  getChildOrganizationsByParent(
    parentOrganizationId: number,
    query?: ChildOrganizationQuery
  ): Observable<ApiListResponse<ChildOrganizationDto>> {
    const endpoint = `${this.BASE_URL}/auditlog/get-child-organizations-by-parent/${parentOrganizationId}`;
    return this.http
      .get<ApiListResponse<ChildOrganizationDto>>(endpoint, {
        params: this.buildChildOrgQueryParams(query || {}),
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private buildChildOrgQueryParams(query: ChildOrganizationQuery): HttpParams {
    let params = new HttpParams();

    if (query.pageNumber !== undefined) {
      params = params.set('Page', query.pageNumber.toString());
    }
    if (query.pageSize !== undefined) {
      params = params.set('PageSize', query.pageSize.toString());
    }
    if (query.sort) {
      params = params.set('Sort', query.sort);
    }
    if (query.sortOrder !== undefined) {
      params = params.set('SortOrder', query.sortOrder.toString());
    }
    if (query.searchTerm) {
      params = params.set('SearchTerm', query.searchTerm);
    }
    return params;
  }

  private buildQueryParams(query: AuditLogQuery): HttpParams {
    let params = new HttpParams();

    params = this.setParamIfDefined(params, 'Page', query.pageNumber);
    params = this.setParamIfDefined(params, 'PageSize', query.pageSize);
    params = this.setParamIfTruthy(params, 'Sort', query.sort);
    params = this.setParamIfDefined(params, 'SortOrder', query.sortOrder);
    params = this.setParamIfTruthy(
      params,
      'OrganizationId',
      query.organizationId
    );
    params = this.setParamIfDefined(params, 'UserId', query.userId);
    params = this.setParamIfDefined(
      params,
      'AuditModuleId',
      query.auditModuleId
    );
    params = this.setParamIfDefined(
      params,
      'AuditOperationTypeId',
      query.auditOperationTypeId
    );
    params = this.setParamIfTruthy(params, 'EntityType', query.entityType);
    params = this.setParamIfDefined(params, 'EntityId', query.entityId);
    params = this.setParamIfTruthy(params, 'DateFrom', query.dateFrom);
    params = this.setParamIfTruthy(params, 'DateTo', query.dateTo);
    params = this.setParamIfTruthy(params, 'SearchTerm', query.searchTerm);
    params = this.setParamIfDefined(
      params,
      'excludeLoginLogs',
      query.excludeLoginLogs
    );
    params = this.setParamIfTruthy(params, 'StudentId', query.studentId);
    params = this.setParamIfTruthy(params, 'StudentName', query.studentName);

    return params;
  }

  /**
   * Helper to set a parameter if the value is not undefined.
   */
  private setParamIfDefined(
    params: HttpParams,
    key: string,
    value: unknown
  ): HttpParams {
    if (value !== undefined) {
      return params.set(key, value === null ? '' : value.toString());
    }
    return params;
  }

  /**
   * Helper to set a parameter if the value is truthy.
   */
  private setParamIfTruthy(
    params: HttpParams,
    key: string,
    value: unknown
  ): HttpParams {
    if (value) {
      return params.set(key, value.toString());
    }
    return params;
  }

  getUsersByOrganization(
    query: UserByOrganizationQuery
  ): Observable<ApiListResponse<UserByOrganizationDto>> {
    const endpoint = `${this.BASE_URL}/auditlog/get-users-by-organization`;
    return this.http
      .get<ApiListResponse<UserByOrganizationDto>>(endpoint, {
        params: this.buildUserQueryParams(query),
      })
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }

  private buildUserQueryParams(query: UserByOrganizationQuery): HttpParams {
    let params = new HttpParams();

    if (query.organizationId) {
      params = params.set('organizationId', query.organizationId.toString());
    }

    if (query.page !== undefined) {
      params = params.set('Page', query.page.toString());
    }
    if (query.pageSize !== undefined) {
      params = params.set('PageSize', query.pageSize.toString());
    }
    if (query.sort) {
      params = params.set('Sort', query.sort);
    }
    if (query.sortOrder !== undefined) {
      params = params.set('SortOrder', query.sortOrder.toString());
    }
    if (query.searchTerm) {
      params = params.set('SearchTerm', query.searchTerm);
    }

    return params;
  }

  getAuditLogModule(): Observable<ApiResponse<AuditModules[]>> {
    return this.http
      .get<ApiResponse<AuditModules[]>>(`${this.BASE_URL}/auditlog/get-modules`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
  getAuditLogOperationtype(): Observable<ApiResponse<AuditModules[]>> {
    return this.http
      .get<
        ApiResponse<AuditModules[]>
      >(`${this.BASE_URL}/auditlog/get-operations`)
      .pipe(catchError(error => this.errorHandler.handleError(error)));
  }
}
